import { TripOriginRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { SummaryMessageUnit } from 'generated/graphql';
import { useToggle } from 'react-use';

const DEFAULT_VISIBLE_SUMMARIES_COUNT = 4;

interface FeedbackSummaryListProps {
  summaryUnits: SummaryMessageUnit[] | undefined;
  shouldRenderShowAllToggle?: boolean;
  shouldShowAllByDefault?: boolean;
  bulletIconColor?: string;
  onClickMore?: () => void;
}

export const FeedbackSummaryList = ({
  summaryUnits,
  shouldRenderShowAllToggle = false,
  shouldShowAllByDefault = false,
  bulletIconColor,
  onClickMore,
}: FeedbackSummaryListProps) => {
  const [shouldShowAll, toggleShouldShowAll] = useToggle(
    shouldShowAllByDefault,
  );

  if (!summaryUnits?.length) {
    return null;
  }

  const allSummaryUnits = summaryUnits;

  const visibleSummaryUnits = allSummaryUnits.slice(
    0,
    DEFAULT_VISIBLE_SUMMARIES_COUNT,
  );
  const remainingSummaryUnits = allSummaryUnits.slice(
    DEFAULT_VISIBLE_SUMMARIES_COUNT,
  );

  const summaryUnitsToDisplay = shouldShowAll
    ? allSummaryUnits
    : visibleSummaryUnits;

  if (summaryUnitsToDisplay.length === 0) {
    return (
      <Typography textAlign="center" variant="subtitle1" color="text.secondary">
        No summary found.
      </Typography>
    );
  }

  if (summaryUnitsToDisplay.length === 1) {
    return (
      <List dense sx={{ pt: 0 }}>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{
              variant: 'body2',
            }}
            primary={summaryUnitsToDisplay[0].text}
          />
        </ListItem>
      </List>
    );
  }

  return (
    <Stack spacing={1}>
      <List dense disablePadding>
        {summaryUnitsToDisplay.map((summaryUnit, index) => (
          <ListItem
            key={summaryUnit.text}
            divider={index < summaryUnitsToDisplay.length - 1}
          >
            <ListItemIcon
              sx={{
                minWidth: '40px',
              }}
            >
              <TripOriginRounded
                sx={{
                  width: 14,
                  height: 14,
                  color: bulletIconColor ?? 'info.main',
                }}
              />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                variant: 'body2',
              }}
              primary={summaryUnit.text}
            />
          </ListItem>
        ))}
      </List>
      {remainingSummaryUnits.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          pb={1.5}
        >
          <Box>
            {!shouldShowAll && (
              <Chip
                label={`+${remainingSummaryUnits.length} more`}
                onClick={onClickMore}
              />
            )}
          </Box>
          {shouldRenderShowAllToggle && (
            <Button onClick={toggleShouldShowAll} size="small">
              {shouldShowAll ? 'Show Less' : 'Show Full Summary'}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
};
